import type { SandpackClient } from "@codesandbox/sandpack-client";
import { Box, Button, Icon, Stack, Text, Tooltip } from "prism-react";
import * as React from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { executeVSCodeCommand } from "utils/vscode";

import { SpecFile } from "./SpecFile";
import { Summary } from "./Summary";
import { StyledToggleButton, StyledContainer } from "./common";
import type { Spec, Test, TestResults, TestRunnerStatus } from "./types";
import {
  flatMap,
  getDuration,
  getAllTestResults,
  getAllSuiteResults,
  splitTail,
  set,
} from "./utils";

interface State {
  specs: Record<string, Spec>;
  status: TestRunnerStatus;
  verbose: boolean;
  watchMode: boolean;
  singleFilePath: string | undefined;
  specsCount: number;
}

const INITIAL_STATE: State = {
  specs: {},
  status: "initialising",
  verbose: true,
  watchMode: true,
  singleFilePath: undefined,
  specsCount: 0,
};

const testFileRegex = /.*\.(test|spec)\.[tj]sx?$/;

export const TestRunner: React.FC<
  {
    client: SandpackClient;
  } & React.HtmlHTMLAttributes<HTMLDivElement>
> = ({ client }) => {
  const [state, setState] = React.useState<State>(INITIAL_STATE);
  const track = useAnalytics();

  const runAllTests = React.useCallback((): void => {
    setState((oldState) => ({
      ...oldState,
      status: "running",
      specs: {},
    }));

    if (client) {
      client.dispatch({ type: "run-all-tests" });
    }
  }, [client]);

  const runSpec = React.useCallback((): void => {
    if (client && state.singleFilePath) {
      setState((oldState) => ({
        ...oldState,
        status: "running",
        specs: {},
      }));

      client.dispatch({ type: "run-tests", path: state.singleFilePath });
    }
  }, [client, state.singleFilePath]);

  const run = React.useCallback((): void => {
    if (state.singleFilePath) {
      runSpec();
    } else {
      runAllTests();
    }
  }, [runSpec, runAllTests, state.singleFilePath]);

  React.useEffect(() => {
    let currentDescribeBlocks: string[] = [];
    let currentSpec = "";

    const unsubscribe = client.listen((data): void => {
      // Note: short-circuit if message isn't for the currently active spec when `pinFile` is true
      if (
        state.singleFilePath &&
        (("path" in data && data.path !== state.singleFilePath) ||
          ("test" in data &&
            "path" in data.test &&
            data.test.path !== state.singleFilePath))
      ) {
        return;
      }

      if (
        data.type === "action" &&
        data.action === "clear-errors" &&
        data.source === "jest"
      ) {
        currentSpec = data.path;
        return;
      }

      if (data.type === "test") {
        if (data.event === "initialize_tests") {
          currentDescribeBlocks = [];
          currentSpec = "";
          if (state.watchMode) {
            return run();
          } else {
            return setState((oldState) => ({
              ...oldState,
              status: "idle",
              specs: {},
            }));
          }
        }

        if (data.event === "test_count") {
          return setState((oldState) => ({
            ...oldState,
            specsCount: data.count,
          }));
        }

        if (data.event === "total_test_start") {
          currentDescribeBlocks = [];
          return setState((oldState) => ({ ...oldState, status: "running" }));
        }

        if (data.event === "total_test_end") {
          return setState((oldState) => {
            return {
              ...oldState,
              status: "complete",
            };
          });
        }

        if (data.event === "add_file") {
          return setState(
            set(["specs", data.path], {
              describes: {},
              tests: {},
              name: data.path,
            }),
          );
        }

        if (data.event === "remove_file") {
          return setState((oldState) => {
            const specs = Object.entries(oldState.specs).reduce(
              (acc, [key, value]) => {
                if (key === data.path) {
                  return acc;
                } else {
                  return { ...acc, [key]: value };
                }
              },
              {},
            );

            return { ...oldState, specs };
          });
        }

        if (data.event === "file_error") {
          return setState(set(["specs", data.path, "error"], data.error));
        }

        if (data.event === "describe_start") {
          currentDescribeBlocks.push(data.blockName);
          const [describePath, currentDescribe] = splitTail(
            currentDescribeBlocks,
          );
          const spec = currentSpec;

          if (currentDescribe === undefined) {
            return;
          }

          return setState(
            set(
              [
                "specs",
                spec,
                "describes",
                ...flatMap(describePath, (name) => [name, "describes"]),
                currentDescribe,
              ],
              {
                name: data.blockName,
                tests: {},
                describes: {},
              },
            ),
          );
        }

        if (data.event === "describe_end") {
          currentDescribeBlocks.pop();
          return;
        }

        if (data.event === "add_test") {
          const [describePath, currentDescribe] = splitTail(
            currentDescribeBlocks,
          );
          const test: Test = {
            status: "idle",
            errors: [],
            name: data.testName,
            blocks: [...currentDescribeBlocks],
            path: data.path,
          };
          if (currentDescribe === undefined) {
            return setState(
              set(["specs", data.path, "tests", data.testName], test),
            );
          } else {
            return setState(
              set(
                [
                  "specs",
                  data.path,
                  "describes",
                  ...flatMap(describePath, (name) => [name, "describes"]),
                  currentDescribe,
                  "tests",
                  data.testName,
                ],
                test,
              ),
            );
          }
        }

        if (data.event === "test_start") {
          const { test } = data;
          const [describePath, currentDescribe] = splitTail(test.blocks);

          const startedTest: Test = {
            status: "running",
            name: test.name,
            blocks: test.blocks,
            path: test.path,
            errors: [],
          };

          if (currentDescribe === undefined) {
            return setState(
              set(["specs", test.path, "tests", test.name], startedTest),
            );
          } else {
            return setState(
              set(
                [
                  "specs",
                  test.path,
                  "describes",
                  ...flatMap(describePath, (name) => [name, "describes"]),
                  currentDescribe,
                  "tests",
                  test.name,
                ],
                startedTest,
              ),
            );
          }
        }

        if (data.event === "test_end") {
          const { test } = data;
          const [describePath, currentDescribe] = splitTail(test.blocks);
          const endedTest = {
            status: test.status,
            errors: test.errors,
            duration: test.duration,
            name: test.name,
            blocks: test.blocks,
            path: test.path,
          };

          if (currentDescribe === undefined) {
            return setState(
              set(["specs", test.path, "tests", test.name], endedTest),
            );
          } else {
            return setState(
              set(
                [
                  "specs",
                  test.path,
                  "describes",
                  ...flatMap(describePath, (name) => [name, "describes"]),
                  currentDescribe,
                  "tests",
                  test.name,
                ],
                endedTest,
              ),
            );
          }
        }
      }
    });

    return unsubscribe;
  }, [state.singleFilePath, state.watchMode, run, client]);

  React.useEffect(
    function watchMode() {
      if (!state.watchMode) {
        return;
      }

      const unsunscribe = client.listen(({ type }) => {
        if (type === "done") {
          run();
        }
      });

      return unsunscribe;
    },
    [state.watchMode, run, client],
  );

  const openSpec = (file: string): void => {
    executeVSCodeCommand("codesandbox.openCsbTab", {
      type: "FILE",
      filepath: file as `$RELATIVE/${string}`,
      view: "code",
    });
  };

  const setSingleFilePathMode = (file: string): void => {
    track("test_run_only_this_file");

    const singleFilePath = file === state.singleFilePath ? undefined : file;

    setState((oldState) => ({
      ...oldState,
      singleFilePath,
    }));

    // useEffect will run tests again
  };

  React.useEffect(() => {
    run();
  }, [run, state.singleFilePath]);

  const [deferredValue, setDefferedValue] = React.useState(state);

  React.useEffect(() => {
    // Immediately update the state
    if (state.status === "complete") {
      setDefferedValue(state);
    } else {
      const timer = setTimeout(() => {
        setDefferedValue(state);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [state]);

  const specs = Object.values(deferredValue.specs);
  const duration = getDuration(specs);
  const testResults = getAllTestResults(specs);
  const suiteResults = getAllSuiteResults(specs);

  return (
    <>
      <Box
        css={{
          display: "flex",
          height: "$11",
          px: "$2",
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "var(--vscode-tab-activeBackground, $neutral-fg-base)",
        }}
      >
        <Stack css={{ width: "100%" }} gap={1} horizontal>
          <Tooltip content="Run once">
            <Button
              icon="play"
              onClick={() => {
                track("test_run_test");
                run();
              }}
            >
              Run
            </Button>
          </Tooltip>

          <Box css={{ flex: 1 }} />

          <HeaderSummary
            loading={deferredValue.status !== "complete"}
            results={testResults}
          />

          <Box css={{ flex: 1 }} />

          <Tooltip content="Run in watch mode">
            <StyledToggleButton
              data-active={state.watchMode}
              icon={state.watchMode ? "eyeOpen" : "eyeClose"}
              onClick={() => {
                track("test_toggle_watch");

                setState((prev) => ({
                  ...prev,
                  watchMode: !prev.watchMode,
                }));

                run();
              }}
              type="button"
            />
          </Tooltip>

          <Tooltip content="Toggle test details">
            <StyledToggleButton
              data-active={state.verbose}
              icon="configs"
              onClick={() => {
                track("test_toggle_description");

                setState((prev) => ({
                  ...prev,
                  verbose: !prev.verbose,
                }));
              }}
              type="button"
            />
          </Tooltip>
        </Stack>
      </Box>

      <StyledContainer css={{ padding: "$4" }} selectable>
        {specs.length === 0 && deferredValue.status === "complete" ? (
          <Box css={{ fontWeight: "$semibold" }}>
            <p>No test files found.</p>
            <p>
              Test match:{" "}
              <Text color="informative-error-base">
                {testFileRegex.toString()}
              </Text>
            </p>
          </Box>
        ) : (
          <Stack gap={8}>
            <Stack gap={2}>
              {specs.map((spec, index) => (
                <SpecFile
                  key={index}
                  currentSingleFilePath={state.singleFilePath}
                  openSpec={openSpec}
                  setSingleFilePathMode={setSingleFilePathMode}
                  spec={spec}
                  verbose={deferredValue.verbose}
                />
              ))}
            </Stack>

            {deferredValue.status === "complete" && testResults.total > 0 && (
              <Summary
                duration={duration}
                suites={suiteResults}
                tests={testResults}
              />
            )}
          </Stack>
        )}
      </StyledContainer>
    </>
  );
};

const HeaderSummary = ({
  loading,
  results: { fail, pass, total },
}: {
  loading: boolean;
  results: TestResults;
}) => {
  if (loading) {
    return (
      <Stack horizontal>
        <Icon size={3} type="progress" />
        <Text>Running...</Text>
      </Stack>
    );
  }

  return (
    <Stack horizontal>
      {pass > 0 && <Text color="informative-success-base">{pass} passed</Text>}
      {fail > 0 && <Text color="informative-error-base">{fail} failed</Text>}
      <Text color="neutral-fg-low">-</Text>
      <Text color="neutral-fg-low">{total} total</Text>
    </Stack>
  );
};
